import { Bank, HelpCircle, InfoCircle, Mail01, PhoneCall01, XClose } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CAN from 'src/assets/img/flags/Canada-sm.svg';
import US from 'src/assets/img/flags/US-sm.svg';
import CommonModal from 'src/components/CommonModal';
import Radio from 'src/components/Radio/Radio';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CURRENCY, QUOTE_URGENCY } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { RootState } from 'src/redux/store';
import { copyToClipboard, usePhone } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { addQuoteServiceLevel, addQuoteServiceType, setAddQuoteData } from '../AddQuote/AddQuote.slice';
import { SERVICE_LEVELS, SERVICE_TYPES } from '../CreateQuote/Steps/Steps.const';

interface IProps {
    handleClose: () => void;
}

// Client decision — the billing account dropdown shows the currency's flag (matches the reference
// design), both in the closed value and the open option list (SelectBox's option.icon renders in
// both via formatOptionLabel).
const getCurrencyFlagIcon = (currency: string | null) => {
    if (currency === CURRENCY.CAD) return <img src={CAN} alt="Canada" className="w-4 h-4 object-cover rounded flex-none" />;
    if (currency === CURRENCY.USD) return <img src={US} alt="United States" className="w-4 h-4 object-cover rounded flex-none" />;

    return null;
};

const AddQuoteBasicDetailsModal = ({ handleClose }: IProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { getFormatPhoneWithPlus } = usePhone();
    const salesRepPhoneRef = useRef<HTMLParagraphElement>(null);
    const salesRepEmailRef = useRef<HTMLParagraphElement>(null);

    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const serviceType = useSelector(addQuoteServiceType);
    const serviceLevel = useSelector(addQuoteServiceLevel);
    const divisions = currentUser?.divisions || [];

    const divisionOptions = useMemo(
        () =>
            divisions.map((div: any) => ({
                label: div.displayName,
                value: div.id,
            })),
        [divisions]
    );

    const hasSingleDivision = divisionOptions.length === 1;

    // Auto-select a division: the only one when a single division exists (previous behaviour);
    // with multiple divisions, the first that has a CAD billing account — so the CAD-default
    // effect below can preselect that account too. No CAD in any division → leave unselected,
    // as before. Currency is upper-cased here because the stored column is lower-case on
    // virtually every row.
    const [selectedDivision, setSelectedDivision] = useState<any>(() => {
        if (hasSingleDivision) return divisionOptions[0];

        const cadDivision = divisions.find((div: any) => (div?.customerBillingAccount || []).some((acc: any) => acc?.currency?.toUpperCase() === CURRENCY.CAD));

        return cadDivision ? { label: cadDivision.displayName, value: cadDivision.id } : null;
    });

    // Billing account options — recalculated whenever division changes
    const billingAccountOptions = useMemo(() => {
        if (!selectedDivision) return [];
        const div = divisions.find((d: any) => d.id === selectedDivision.value);
        const accounts = div?.customerBillingAccount || [];

        return accounts.map((acc: any) => {
            const currency = acc.currency?.toUpperCase() ?? null;

            return {
                label: `${acc.currency?.toUpperCase()} Billing Account${acc.quickBookId ? ` ${acc.quickBookId}` : ''}`,
                value: acc.id,
                currency,
                exists: true,
                icon: getCurrencyFlagIcon(currency),
            };
        });
    }, [selectedDivision, divisions]);

    // Client decision — when a division has exactly one billing account, the OTHER currency shows
    // as an "Add - <currency> Billing Account" entry (SelectBox's own isNewOption/handleCreate —
    // same mechanism as "+ Add New Shipper Contact" elsewhere in the app), not a normal selectable
    // option. Picking it can't set a value (SelectBox intercepts it and calls handleCreate instead
    // of onChangeFunc), so it just opens the informational modal below.
    const missingCurrency = useMemo(() => {
        if (billingAccountOptions.length !== 1) return null;

        return billingAccountOptions[0].currency === CURRENCY.USD ? CURRENCY.CAD : CURRENCY.USD;
    }, [billingAccountOptions]);

    const [selectedBillingAccount, setSelectedBillingAccount] = useState<any>(null);
    const [showMissingBillingAccountModal, setShowMissingBillingAccountModal] = useState(false);

    // Client decision — CAD is the default billing account whenever the division has one, since
    // most customers bill in CAD; the dropdown stays open so a customer can switch to USD. Falls
    // back to the previous behaviour (auto-select only when the division has a single account) when
    // there is no CAD account at all, so a USD-only division still auto-selects its USD account.
    // Compares `option.currency`, which the options memo has already upper-cased — the stored
    // `currency` is lower-case on virtually every row, so comparing the raw column would match
    // almost nothing.
    useEffect(() => {
        const cadAccount = billingAccountOptions.find((option) => option.currency === CURRENCY.CAD);

        setSelectedBillingAccount(cadAccount ?? (billingAccountOptions.length === 1 ? billingAccountOptions[0] : null));
    }, [billingAccountOptions]);

    const [selectedServiceType, setSelectedServiceType] = useState<any>(serviceType ? SERVICE_TYPES.find((s) => s.value === serviceType) ?? null : null);
    // Always holds a value (Standard preselected), so it never blocks the submit gate below.
    const [selectedServiceLevel, setSelectedServiceLevel] = useState<string>(serviceLevel ?? 'standard');

    // Multi-stop route selector — default is 2 stops (direct pickup -> delivery), matching today's behavior.
    // n is the total stop count (shipper + intermediate stops + consignee); up to 5 intermediate stops = n up to 7.
    const STOP_COUNT_OPTIONS = [2, 3, 4, 5, 6, 7].map((n) => ({
        label: n === 2 ? t('MyQuote.Stops_Direct_Option') : n - 2 === 1 ? t('MyQuote.Stops_Via_One') : t('MyQuote.Stops_Via_Many', { count: n - 2 }),
        value: n,
    }));
    const [selectedStopCount, setSelectedStopCount] = useState<any>(STOP_COUNT_OPTIONS[0]);

    // Track whether user has attempted to submit (to show red borders)
    const [submitted, setSubmitted] = useState(false);

    const isAllFilled = !!selectedDivision && !!selectedBillingAccount && !!selectedServiceType;

    // Client decision — the missing-billing-account modal should point the customer at their actual
    // sales contact (name/phone/email as plain text under the description, no card/box treatment)
    // instead of a generic "contact your account manager" line. Falls back to that generic wording
    // when this customer has no salesRep on file at all.
    const salesRep = currentUser?.salesRep;
    const hasSalesRepContact = !!(salesRep?.salesrepname || salesRep?.phone || salesRep?.salesrepemail);
    const missingAccountDescription = t(hasSalesRepContact ? 'MyQuote.Billing_Account_Not_Set_Up_Desc_Short' : 'MyQuote.Billing_Account_Not_Set_Up_Desc', {
        currency: missingCurrency,
    } as any) as unknown as string;
    // Force the line break right after "sales rep." (matches Figma's fixed 2-line layout) instead
    // of leaving it to the container width — a width-only wrap point sits on a knife's edge here
    // (line 2 is about as wide as line 1 plus its next word), so it'd be one resize away from
    // reflowing to 1 or 3 lines.
    const missingAccountDescriptionBreakIndex = missingAccountDescription.indexOf('. ');
    const missingAccountDescriptionLine1 = missingAccountDescriptionBreakIndex === -1 ? missingAccountDescription : missingAccountDescription.slice(0, missingAccountDescriptionBreakIndex + 1);
    const missingAccountDescriptionLine2 = missingAccountDescriptionBreakIndex === -1 ? '' : missingAccountDescription.slice(missingAccountDescriptionBreakIndex + 2);

    const handleCreateQuote = () => {
        setSubmitted(true);

        if (!isAllFilled) return;

        dispatch(
            setAddQuoteData({
                data: {
                    divisionId: selectedDivision.value,
                    customerBillingAccountId: selectedBillingAccount.value,
                    serviceType: selectedServiceType.value,
                    urgency: QUOTE_URGENCY.LIVE,
                    serviceLevel: selectedServiceLevel,
                    stopCount: selectedStopCount.value,
                    stops: {},
                },
            })
        );

        handleClose();
        navigate(PATH.ADD_QUOTE, { state: { fromNewQuote: true } });
    };

    return (
        <>
            <CommonModal
                title={t('MyQuote.New_Quote')}
                handleClose={handleClose}
                size="max-w-[600px]"
                primaryBtnText={t('MyQuote.Create_Quote')}
                primaryBtnOnClick={handleCreateQuote}
                primaryBtnLoading={false}
                primaryBtnDisabled={!isAllFilled}
                secondaryBtnText={t('Common.Cancel')}
                secondaryBtnOnClick={handleClose}
            >
                <div className="flex flex-col gap-3 p-4">
                    <div className="rounded-lg border border-utilityGray200 flex flex-col w-full bg-gray25 px-3 py-3 space-y-3">
                        {/* Client decision — always show Division/Billing Account, even with only one
                        option. Previously hidden (auto-selected silently) when there was a single
                        option; still auto-selected as the default below, just now visible. */}
                        <SelectBox
                            label={t('MyQuote.Select_Division')}
                            labelClassName="form_label !mb-1.5"
                            name="division"
                            placeholder={t('MyQuote.Select_Division_Placeholder')}
                            options={divisionOptions}
                            value={selectedDivision}
                            onChangeFunc={(option: any) => setSelectedDivision(option)}
                            isError={submitted && !selectedDivision}
                            required
                        />

                        <SelectBox
                            label={t('MyQuote.Select_Account')}
                            labelClassName="form_label !mb-1.5"
                            name="billingAccount"
                            placeholder={t('MyQuote.Select_Billing_Account_Placeholder')}
                            options={billingAccountOptions}
                            value={selectedBillingAccount}
                            isDisabled={!selectedDivision}
                            onChangeFunc={(option: any) => setSelectedBillingAccount(option)}
                            isNewOption={!!missingCurrency}
                            newOptionText={t('MyQuote.Add_Missing_Billing_Account', { currency: missingCurrency })}
                            newOptionPosition="last"
                            handleCreate={() => setShowMissingBillingAccountModal(true)}
                            isError={submitted && !selectedBillingAccount}
                            required
                        />

                        {/* Service Type */}
                        <div className="flex flex-col ">
                            <label className="form_label !mb-1.5">
                                {t('MyQuote.Select_Service_Type')}
                                <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                            </label>
                            <div className="flex gap-3">
                                {SERVICE_TYPES.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = selectedServiceType?.value === option.value;
                                    const isError = submitted && !selectedServiceType;

                                    return (
                                        <label
                                            key={option.value}
                                            className={`flex-1 cursor-pointer rounded-md p-3 flex flex-col border transition-colors ${
                                                isSelected ? 'border-borderSecondary bg-utilityIndigo25' : isError ? 'border-red-500' : 'border-utilityGray200'
                                            }`}
                                        >
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className="text-[12px] leading-[18px] font-normal text-grayLight600">{option.title}</span>
                                                <Radio
                                                    inputName="serviceType"
                                                    value={option.value}
                                                    checked={isSelected}
                                                    onChangeFunc={() => setSelectedServiceType({ label: option.label, value: option.value })}
                                                />
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <IconComponent className="w-4 h-4 text-primary" />
                                                <span className="text-sm font-medium text-textSecondary">{option.label}</span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Service Level (meeting 2026-10-01): same card pattern as Service Type above */}
                        <div className="flex flex-col ">
                            <label className="form_label !mb-1.5">
                                {t('MyQuote.Select_Service_Level')}
                                <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                            </label>
                            <div className="flex gap-3">
                                {SERVICE_LEVELS.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = selectedServiceLevel === option.value;

                                    return (
                                        <label
                                            key={option.value}
                                            className={`flex-1 cursor-pointer rounded-md p-3 flex flex-col border transition-colors ${
                                                isSelected ? 'border-borderSecondary bg-utilityIndigo25' : 'border-utilityGray200'
                                            }`}
                                        >
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className="text-[12px] leading-[18px] font-normal text-grayLight600">{t(option.titleKey)}</span>
                                                <Radio inputName="serviceLevel" value={option.value} checked={isSelected} onChangeFunc={() => setSelectedServiceLevel(option.value)} />
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <IconComponent className="w-4 h-4 text-primary" />
                                                <span className="text-sm font-medium text-textSecondary">{t(option.labelKey)}</span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Stop count — additive: defaults to 2 (direct), stepper wiring lands in later tasks */}
                        <div className="flex flex-col">
                            <div className="flex items-center gap-x-1.5 mb-1.5">
                                <label className="form_label mb-0">
                                    {t('MyQuote.How_Many_Stops')}
                                    <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>
                                </label>
                                <TooltipCmp message={t('MyQuote.Stops_Helper')}>
                                    <InfoCircle className="w-3.5 h-3.5 text-primary cursor-pointer" />
                                </TooltipCmp>
                            </div>
                            <SelectBox name="stopCount" options={STOP_COUNT_OPTIONS} value={selectedStopCount} onChangeFunc={(option: any) => setSelectedStopCount(option)} />
                        </div>
                    </div>
                </div>
            </CommonModal>
            {showMissingBillingAccountModal && (
                <CommonModal
                    handleClose={() => setShowMissingBillingAccountModal(false)}
                    size="max-w-[600px]"
                    secondaryBtnText={t('Common.Dismiss')}
                    secondaryBtnOnClick={() => setShowMissingBillingAccountModal(false)}
                >
                    {/* Client decision — matches the approved Figma reference (node 6128:13192): a
                    centered icon+title+description header instead of CommonModal's default
                    left-aligned bar, so `title` is intentionally omitted and the header/close button
                    are built here. Sales rep contact is shown as two peer Contact Number/Contact
                    Email cards (no photo/name), followed by a Step-by-Step Guide box. */}
                    <div className="flex flex-col items-center relative w-full">
                        <div
                            className="absolute top-4 right-4 cursor-pointer bg-transparent hover:bg-[#e5e5e5] transition-all duration-300 h-8 w-8 flex justify-center items-center rounded-full flex-none"
                            onClick={() => setShowMissingBillingAccountModal(false)}
                        >
                            <XClose className="h-4 w-4 text-textSecondary" />
                        </div>
                        <div className="flex flex-col gap-4 items-center pt-6 px-6 w-full">
                            <div className="w-12 h-12 rounded-md bg-utilityGray100 border border-utilityBlack border-opacity-[0.08] flex items-center justify-center flex-none">
                                <Bank className="w-6 h-6 text-textSecondary" />
                            </div>
                            <div className="flex flex-col gap-1 items-center w-full">
                                <p className="text-grayLight900 text-lg font-semibold text-center w-full">
                                    {t('MyQuote.Billing_Account_Not_Set_Up_Title', { currency: missingCurrency } as any) as unknown as string}
                                </p>
                                <p className="text-grayLight900 text-sm font-normal text-center w-full px-[50px]">
                                    {missingAccountDescriptionLine1}
                                    <br />
                                    {missingAccountDescriptionLine2}
                                </p>
                            </div>
                        </div>
                        <div className="h-5 w-full flex-none" />
                    </div>

                    {hasSalesRepContact && (
                        <div className="flex flex-col gap-3 items-start pb-4 px-6 w-full">
                            <div className="flex flex-col gap-1.5 items-start w-full">
                                <p className="text-black text-xs font-medium">{t('Common.Sales_Representative')}</p>
                                <div className="flex items-stretch gap-3 w-full">
                                    {salesRep?.phone && (
                                        <div className="flex-1 min-w-[260px] rounded-lg border border-utilityGray200 bg-gray25 shadow-xs overflow-hidden">
                                            <div className="flex gap-3 items-center p-4">
                                                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                                    <p className="text-grayLight900 text-sm font-medium">{t('Common.Contact_Number')}</p>
                                                    <TruncateTextCmp message={getFormatPhoneWithPlus(salesRep.phone)} refName={salesRepPhoneRef} truncateTextParentClassName="w-full">
                                                        <p ref={salesRepPhoneRef} className="text-grayLight600 text-xs font-normal truncate leading-[18px]">
                                                            {getFormatPhoneWithPlus(salesRep.phone)}
                                                        </p>
                                                    </TruncateTextCmp>
                                                </div>

                                                <TooltipCmp message={t('Common.Copy')} childrenClassName="flex">
                                                    <button
                                                        type="button"
                                                        className="flex-none text-grayLight600 hover:text-primary"
                                                        onClick={() => {
                                                            copyToClipboard(getFormatPhoneWithPlus(salesRep.phone));
                                                            WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                                                        }}
                                                    >
                                                        <PhoneCall01 className="w-5 h-5" />
                                                    </button>
                                                </TooltipCmp>
                                            </div>
                                        </div>
                                    )}

                                    {salesRep?.salesrepemail && (
                                        <div className="flex-1 min-w-[260px] rounded-lg border border-utilityGray200 bg-gray25 shadow-xs overflow-hidden">
                                            <div className="flex gap-3 items-center p-4">
                                                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                                    <p className="text-grayLight900 text-sm font-medium">{t('Common.Contact_Email')}</p>
                                                    <TruncateTextCmp message={salesRep.salesrepemail} refName={salesRepEmailRef} truncateTextParentClassName="w-full">
                                                        <p ref={salesRepEmailRef} className="text-grayLight600 text-xs font-normal truncate leading-[18px]">
                                                            {salesRep.salesrepemail}
                                                        </p>
                                                    </TruncateTextCmp>
                                                </div>
                                                <TooltipCmp message={t('Common.Copy')} childrenClassName="flex">
                                                    <button
                                                        type="button"
                                                        className="flex-none text-grayLight600 hover:text-primary"
                                                        onClick={() => {
                                                            copyToClipboard(salesRep.salesrepemail);
                                                            WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                                                        }}
                                                    >
                                                        <Mail01 className="w-5 h-5" />
                                                    </button>
                                                </TooltipCmp>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2 items-start p-3 rounded-xl border border-utilityGray200 bg-gray25 w-full">
                                <div className="flex gap-1.5 items-center w-full">
                                    <HelpCircle className="w-4 h-4 text-grayLight600 flex-none" />
                                    <p className="flex-1 text-grayLight900 text-xs font-medium">{t('MyQuote.Step_By_Step_Guide')}</p>
                                </div>
                                {[
                                    t('MyQuote.Billing_Account_Step_1'),
                                    t('MyQuote.Billing_Account_Step_2', { currency: missingCurrency } as any) as unknown as string,
                                    t('MyQuote.Billing_Account_Step_3'),
                                ].map((step, idx) => (
                                    <div key={idx} className="flex items-start w-full pl-5">
                                        <div className="flex gap-1 items-center w-full">
                                            <span className="text-grayLight600 text-xs font-normal flex-none">{idx + 1}.</span>
                                            <span className="text-grayLight600 text-xs font-normal flex-1">{step}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </CommonModal>
            )}
        </>
    );
};

export default AddQuoteBasicDetailsModal;
